import classNames from 'classnames';
import type { ReactElement, RefObject, Ref } from 'react';
import { useRef } from 'react';
import styles from './PersonDetailsInfo.module.scss';
import Heading from '../../atoms/heading/Heading';
import Text from '../../atoms/text/Text';
import { OfficeEmail } from '../location/OfficeEmail';
import type { PersonContactInfo } from './PersonDetails.data';
import type { TextRef } from '../../atoms/text/Text.data';
import type { HeadingRef } from '../../atoms/heading/Heading.data';
import Link from 'next/link';

type PersonContactInfoProps = {
  className?: string;
  details: PersonContactInfo;
  ref?: Ref<PersonContactInfoRef>;
};
type PersonContactInfoRef = HTMLDivElement;

export default function PersonDetailsInfo({
  className,
  details,
  ref,
}: PersonContactInfoProps): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const labelRef = useRef<HeadingRef>(null);
  const locationRef = useRef<TextRef>(null);

  return (
    <div ref={elementRef} className={classNames(styles.personContactInfo, className)}>
      <div className={styles.locationContainer}>
        {details.location?.story?.name && (
          <>
            {details.label && (
              <Heading
                as="span"
                size="label-small"
                disableTriggerInOnScroll={false}
                transitionInType="slideUpFadeIn"
                transitionOutType="fadeOut"
                className={styles.label}
                ref={labelRef}
              >
                {details.label}
              </Heading>
            )}

            <Text
              as="p"
              size="body-standard"
              disableTriggerInOnScroll={false}
              transitionInType="splitLineRotateIn"
              transitionOutType="fadeOut"
              ref={locationRef}
              className={styles.linkContainer}
            >
              <Link
                href={details.location.url || details.location.cached_url}
                passHref
                target={details.location.target}
                rel="noreferrer"
              >
                {details.location.story.name}
              </Link>
            </Text>
          </>
        )}
      </div>
      {details.emails && details.emails.length > 0 && (
        <ul>
          {details.emails!.map((email) => (
            <OfficeEmail email={email} key={email._uid} />
          ))}
        </ul>
      )}
    </div>
  );
}
